import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import Konva from 'konva';
import { firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../environments/environment';
import { PlanService } from '../services/plan.service';
import { ObjektStrukturService } from '../services/objekt-struktur.service';
import { DokumenteService } from '../services/dokumente.service';
import {
  buildDrawEnvelope,
  parseDrawEnvelope,
  drawJsonFilename,
  backgroundFilename,
  isDrawJsonFile,
  isBackgroundFile
} from './plan-draw.contract';
import {
  PlanDrawIndicatorDialogComponent,
  PlanDrawIndicatorDialogResult
} from './plan-draw-indicator-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel
} from '../@shared/dialog/confirm-dialog/confirm-dialog.component';

type DrawTool =
  | 'pan'
  | 'pen'
  | 'eraser'
  | 'rect-landscape'
  | 'rect-portrait'
  | 'square'
  | 'wall-h'
  | 'wall-v'
  | 'l-left'
  | 'l-right'
  | 'door';

interface PlacedMarker {
  roomIndikatorId: string;
  indicator: any;
  group: Konva.Group;
}

@Component({
  selector: 'app-plan-draw',
  templateUrl: './plan-draw.component.html',
  styleUrls: ['./plan-draw.component.scss']
})
export class PlanDrawComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('konvaHost', { static: false }) konvaHost!: ElementRef<HTMLDivElement>;

  // Route context
  standortId: string | null = null;
  sektionId: string | null = null;
  planId: string | null = null;
  clientId: string | null = null;

  // Mode
  isEditMode = false;
  showStartChoice = true;
  isSaving = false;

  // Toolbar state
  // Default to the hand/move (pan) tool so an accidental click on entering the
  // editor does not start drawing.
  tool: DrawTool = 'pan';
  colors: string[] = ['#111827', '#dc2626', '#2563eb'];
  strokeColor = '#111827';
  baseStrokeWidth = 3;

  // Grid drawing mode: snaps to a coarse grid and constrains segments to
  // horizontal, vertical or 45° diagonal lines. The grid itself is not shown.
  gridMode = false;
  private gridLayer: Konva.Layer | null = null;
  private gridSizeLogical = 200;

  // Konva
  private stage: Konva.Stage | null = null;
  private bgLayer: Konva.Layer | null = null;
  private drawLayer: Konva.Layer | null = null;
  private markerLayer: Konva.Layer | null = null;
  private bgImageNode: Konva.Image | null = null;

  // Resize handle (transformer) for selected shapes
  private transformer: Konva.Transformer | null = null;
  private selectedNode: Konva.Node | null = null;
  private readonly baseAnchorSize = 12;

  // Logical coordinate base (shared contract)
  private logicalWidth = 1654;
  private logicalHeight = 2339;
  private fitScale = 1;
  private offsetX = 0;
  private offsetY = 0;

  // Drawing transient state
  private isDrawing = false;
  private currentLine: Konva.Line | null = null;
  private lastPinchDistance = 0;

  // Background photo
  private pendingBackgroundFile: File | null = null;
  private backgroundUploadId: number | null = null;
  private hasBackground = false;

  // Sektion picker fallback
  needsSektionPicker = false;
  sektionChoices: any[] = [];

  // Indicators
  allRoomIndikatoren: any[] = [];
  private placedMarkers: PlacedMarker[] = [];

  // Plan + resize
  private plan: any = null;
  private resizeObserver?: ResizeObserver;
  private existingDrawJsonUploadId: number | null = null;
  private existingCompositeUploadId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private http: HttpClient,
    private planService: PlanService,
    private objektStrukturService: ObjektStrukturService,
    private dokumenteService: DokumenteService
  ) {}

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParams;
    const params = this.route.snapshot.params;
    this.standortId = qp['standortId'] || null;
    this.sektionId = qp['sektionId'] || null;
    this.planId = params['id'] || qp['planId'] || null;
    this.isEditMode = !!this.planId;
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initStage(), 50);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.stage) {
      this.stage.destroy();
      this.stage = null;
    }
  }

  // ============================================================
  // Stage setup
  // ============================================================
  private initStage(): void {
    const host = this.konvaHost?.nativeElement;
    if (!host) {
      setTimeout(() => this.initStage(), 100);
      return;
    }
    const width = host.clientWidth || 800;
    const height = host.clientHeight || 600;

    this.stage = new Konva.Stage({ container: host, width, height, draggable: this.tool === 'pan' });
    this.bgLayer = new Konva.Layer({ listening: false });
    this.gridLayer = new Konva.Layer({ listening: false, visible: this.gridMode });
    this.drawLayer = new Konva.Layer();
    this.markerLayer = new Konva.Layer();
    this.stage.add(this.bgLayer);
    this.stage.add(this.gridLayer);
    this.stage.add(this.drawLayer);
    this.stage.add(this.markerLayer);

    // Transformer provides a corner handle to resize the selected shape.
    this.transformer = new Konva.Transformer({
      rotateEnabled: false,
      enabledAnchors: ['bottom-right'],
      ignoreStroke: true,
      anchorStroke: '#1d4ed8',
      anchorFill: '#ffffff',
      borderStroke: '#1d4ed8',
      boundBoxFunc: (oldBox, newBox) => {
        // Prevent shrinking a shape below a sensible minimum (screen pixels).
        const min = 14;
        const shrinkingW = newBox.width < oldBox.width;
        const shrinkingH = newBox.height < oldBox.height;
        if ((shrinkingW && newBox.width < min) || (shrinkingH && newBox.height < min)) {
          return oldBox;
        }
        return newBox;
      }
    });
    this.transformer.on('transformend', () => this.normalizeSelected());
    this.drawLayer.add(this.transformer);

    this.computeFit();
    this.applyLayerTransforms();
    this.updateTransformerStyle();
    this.buildGrid();
    this.bindStageEvents();
    this.setupResizeObserver();

    if (this.isEditMode && this.planId) {
      this.showStartChoice = false;
      this.loadExistingPlan();
    } else if (!this.sektionId && this.standortId) {
      this.loadSektionChoices();
    }
  }

  /** When no sektion was passed in, resolve it (auto-select if only one exists). */
  private loadSektionChoices(): void {
    this.objektStrukturService.getSektionenByStandort(this.standortId!).subscribe({
      next: (res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        if (list.length === 1) {
          this.sektionId = list[0].id;
          this.needsSektionPicker = false;
        } else if (list.length > 1) {
          this.sektionChoices = list;
          this.needsSektionPicker = true;
        }
      },
      error: () => {
        this.needsSektionPicker = false;
      }
    });
  }

  private setupResizeObserver(): void {
    const host = this.konvaHost?.nativeElement;
    if (!host || !('ResizeObserver' in window)) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(host);
  }

  private handleResize(): void {
    const host = this.konvaHost?.nativeElement;
    if (!host || !this.stage) {
      return;
    }
    const width = host.clientWidth;
    const height = host.clientHeight;
    if (width > 0 && height > 0) {
      this.stage.width(width);
      this.stage.height(height);
      this.computeFit();
      this.applyLayerTransforms();
      this.updateTransformerStyle();
      this.buildGrid();
      this.repositionMarkers();
      this.stage.batchDraw();
    }
  }

  /** Keep the transformer handles a usable size despite the scaled draw layer. */
  private updateTransformerStyle(): void {
    if (!this.transformer) {
      return;
    }
    const scale = this.fitScale > 0 ? this.fitScale : 1;
    this.transformer.anchorSize(this.baseAnchorSize / scale);
    this.transformer.anchorCornerRadius(3 / scale);
    this.transformer.anchorStrokeWidth(2 / scale);
    this.transformer.borderStrokeWidth(1.5 / scale);
  }

  private computeFit(): void {
    if (!this.stage) {
      return;
    }
    const cw = this.stage.width();
    const ch = this.stage.height();
    this.fitScale = Math.min(cw / this.logicalWidth, ch / this.logicalHeight);
    if (!isFinite(this.fitScale) || this.fitScale <= 0) {
      this.fitScale = 1;
    }
    this.offsetX = (cw - this.logicalWidth * this.fitScale) / 2;
    this.offsetY = (ch - this.logicalHeight * this.fitScale) / 2;
  }

  private applyLayerTransforms(): void {
    [this.bgLayer, this.gridLayer, this.drawLayer].forEach(layer => {
      if (layer) {
        layer.scale({ x: this.fitScale, y: this.fitScale });
        layer.position({ x: this.offsetX, y: this.offsetY });
      }
    });
  }

  /** (Re)build the faint grid overlay covering the whole logical canvas. */
  private buildGrid(): void {
    if (!this.gridLayer) {
      return;
    }
    this.gridLayer.destroyChildren();
    const step = this.gridSizeLogical;
    const w = this.logicalWidth;
    const h = this.logicalHeight;
    const lineColor = '#cbd5e1';
    const strokeW = 1 / (this.fitScale > 0 ? this.fitScale : 1);
    for (let x = 0; x <= w; x += step) {
      this.gridLayer.add(new Konva.Line({ points: [x, 0, x, h], stroke: lineColor, strokeWidth: strokeW, listening: false }));
    }
    for (let y = 0; y <= h; y += step) {
      this.gridLayer.add(new Konva.Line({ points: [0, y, w, y], stroke: lineColor, strokeWidth: strokeW, listening: false }));
    }
    this.gridLayer.batchDraw();
  }

  private snapToGrid(x: number, y: number): { x: number; y: number } {
    const step = this.gridSizeLogical;
    return { x: Math.round(x / step) * step, y: Math.round(y / step) * step };
  }

  private logicalToCanvas(lx: number, ly: number): { x: number; y: number } {
    return { x: lx * this.fitScale + this.offsetX, y: ly * this.fitScale + this.offsetY };
  }

  private canvasToLogical(cx: number, cy: number): { x: number; y: number } {
    return { x: (cx - this.offsetX) / this.fitScale, y: (cy - this.offsetY) / this.fitScale };
  }

  // ============================================================
  // Toolbar
  // ============================================================
  setTool(tool: DrawTool): void {
    this.tool = tool;
    if (this.stage) {
      this.stage.draggable(tool === 'pan');
    }
    // Selection/resizing is only available with the select (pan) tool.
    if (tool !== 'pan') {
      this.deselectNode();
    }
  }

  /** Activate the pen, choosing free-form or grid (snapped H/V/diagonal) mode. */
  setPen(grid: boolean): void {
    this.gridMode = grid;
    // The grid overlay is intentionally never shown; it only governs snapping.
    if (this.gridLayer) {
      this.gridLayer.visible(false);
    }
    this.setTool('pen');
  }

  setColor(color: string): void {
    this.strokeColor = color;
    // Apply the colour to the currently selected shape, if any.
    if (this.selectedNode) {
      if (this.selectedNode instanceof Konva.Group) {
        this.selectedNode.getChildren().forEach((child) => {
          if (typeof (child as any).stroke === 'function') {
            (child as any).stroke(color);
          }
        });
      } else if (typeof (this.selectedNode as any).fill === 'function' && this.selectedNode.name().includes('fill')) {
        (this.selectedNode as any).fill(color);
      } else if (typeof (this.selectedNode as any).stroke === 'function') {
        (this.selectedNode as any).stroke(color);
      }
      this.drawLayer?.batchDraw();
    }
  }

  colorLabel(color: string): string {
    const labels: { [key: string]: string } = {
      '#111827': 'Schwarz',
      '#dc2626': 'Rot',
      '#2563eb': 'Blau'
    };
    return labels[color] || color;
  }

  clearDrawing(): void {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '420px',
      data: new ConfirmDialogModel(
        'Zeichnung löschen',
        'Möchten Sie wirklich die gesamte Zeichnung löschen?\nPlatzierte Indikatoren bleiben erhalten.',
        'Abbrechen',
        'Löschen'
      )
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed && this.drawLayer) {
        this.deselectNode();
        // Remove every drawn node but keep the transformer instance alive.
        this.drawLayer.getChildren()
          .filter((child) => child !== this.transformer)
          .forEach((child) => child.destroy());
        this.drawLayer.draw();
      }
    });
  }

  // ============================================================
  // Selection & resize
  // ============================================================
  private selectNode(node: Konva.Node): void {
    if (!this.transformer) {
      return;
    }
    this.selectedNode = node;
    const name = node.name();
    if (name.includes('line-h')) {
      // Horizontal line: only its length (left/right) may change, never its thickness.
      this.transformer.keepRatio(false);
      this.transformer.enabledAnchors(['middle-left', 'middle-right']);
    } else if (name.includes('line-v')) {
      // Vertical line: only its length (top/bottom) may change, never its thickness.
      this.transformer.keepRatio(false);
      this.transformer.enabledAnchors(['top-center', 'bottom-center']);
    } else {
      // Squares and doors keep their aspect ratio while resizing.
      const keepRatio = name.includes('door') || name.includes('keep-ratio');
      this.transformer.keepRatio(keepRatio);
      this.transformer.enabledAnchors(['bottom-right']);
    }
    this.transformer.nodes([node]);
    this.transformer.moveToTop();
    this.drawLayer?.batchDraw();
  }

  private deselectNode(): void {
    this.selectedNode = null;
    if (this.transformer) {
      this.transformer.nodes([]);
      this.drawLayer?.batchDraw();
    }
  }

  /** After a transform, bake the scale into a rectangle's width/height so the
   *  stroke width stays constant (groups such as doors keep their scale). */
  private normalizeSelected(): void {
    const node = this.selectedNode;
    if (node instanceof Konva.Rect) {
      const newWidth = Math.max(8, node.width() * node.scaleX());
      const newHeight = Math.max(8, node.height() * node.scaleY());
      node.width(newWidth);
      node.height(newHeight);
      node.scaleX(1);
      node.scaleY(1);
      this.drawLayer?.batchDraw();
    } else if (node instanceof Konva.Line) {
      // Bake the scale into the points so the stroke width stays constant.
      const sx = node.scaleX();
      const sy = node.scaleY();
      if (sx !== 1 || sy !== 1) {
        node.points(node.points().map((v, i) => (i % 2 === 0 ? v * sx : v * sy)));
        node.scaleX(1);
        node.scaleY(1);
        this.drawLayer?.batchDraw();
      }
    }
  }

  cancel(): void {
    this.navigateBack();
  }

  private navigateBack(): void {
    // Return to the plan page we came from (the plan-edit view), never the
    // super-admin-only /plan list.
    if (this.planId) {
      this.router.navigate(['/plan', this.planId], {
        queryParams: this.standortId ? { standortId: this.standortId } : {}
      });
      return;
    }
    // Brand-new drawing cancelled before any save: fall back to the standort
    // context if we have one, so we still avoid the /plan list.
    if (this.standortId) {
      this.router.navigate(['/standort-edit', this.standortId], { queryParams: { tab: 'plaene' } });
      return;
    }
    this.router.navigate(['/plan']);
  }

  // ============================================================
  // Drawing interaction (pen / eraser) + zoom
  // ============================================================
  private bindStageEvents(): void {
    if (!this.stage) {
      return;
    }
    this.stage.on('pointerdown', () => this.handlePointerDown());
    this.stage.on('pointermove', () => this.handlePointerMove());
    this.stage.on('pointerup', () => this.handlePointerUp());

    // Selection / resize handling (only with the select tool).
    this.stage.on('click tap', (e) => {
      if (this.tool !== 'pan') {
        return;
      }
      const target = e.target;
      // Clicking empty canvas clears the selection.
      if (!target || target === this.stage) {
        this.deselectNode();
        return;
      }
      // Ignore clicks on the transformer's own anchors/border.
      if (this.transformer && target.getParent() === this.transformer) {
        return;
      }
      // Climb to the top-level node that lives directly in the draw layer.
      let node: Konva.Node = target;
      while (node.getParent() && node.getParent() !== this.drawLayer) {
        node = node.getParent() as Konva.Node;
      }
      if (node.getParent() === this.drawLayer && node !== this.transformer && node.name() && node.name().includes('shape')) {
        this.selectNode(node);
      } else {
        this.deselectNode();
      }
    });

    this.stage.on('wheel', (e) => {
      e.evt.preventDefault();
      const scaleBy = 1.08;
      const oldScale = this.stage!.scaleX();
      const pointer = this.stage!.getPointerPosition();
      if (!pointer) {
        return;
      }
      const mousePointTo = {
        x: (pointer.x - this.stage!.x()) / oldScale,
        y: (pointer.y - this.stage!.y()) / oldScale
      };
      const direction = e.evt.deltaY > 0 ? -1 : 1;
      const newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
      const clamped = Math.max(0.3, Math.min(6, newScale));
      this.stage!.scale({ x: clamped, y: clamped });
      this.stage!.position({
        x: pointer.x - mousePointTo.x * clamped,
        y: pointer.y - mousePointTo.y * clamped
      });
      this.stage!.batchDraw();
    });

    this.stage.on('touchmove', (e) => {
      const touches = (e.evt as TouchEvent).touches;
      if (touches.length === 2) {
        e.evt.preventDefault();
        const p1 = { x: touches[0].clientX, y: touches[0].clientY };
        const p2 = { x: touches[1].clientX, y: touches[1].clientY };
        const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
        if (this.lastPinchDistance > 0) {
          const oldScale = this.stage!.scaleX();
          const newScale = Math.max(0.3, Math.min(6, oldScale * (dist / this.lastPinchDistance)));
          this.stage!.scale({ x: newScale, y: newScale });
          this.stage!.batchDraw();
        }
        this.lastPinchDistance = dist;
      }
    });
    this.stage.on('touchend', () => (this.lastPinchDistance = 0));
  }

  private handlePointerDown(): void {
    if (!this.stage || !this.drawLayer) {
      return;
    }
    if (this.tool === 'eraser') {
      this.eraseAtPointer();
      return;
    }
    if (this.tool !== 'pen') {
      return;
    }
    let pos = this.drawLayer.getRelativePointerPosition();
    if (!pos) {
      return;
    }
    this.isDrawing = true;
    if (this.gridMode) {
      // Orthogonal mode: snap to grid, straight segments only.
      pos = this.snapToGrid(pos.x, pos.y);
      this.currentLine = new Konva.Line({
        stroke: this.strokeColor,
        strokeWidth: this.baseStrokeWidth / this.fitScale,
        lineCap: 'round',
        lineJoin: 'round',
        tension: 0,
        bezier: false,
        points: [pos.x, pos.y],
        name: 'pen-line grid-line'
      });
    } else {
      this.currentLine = new Konva.Line({
        stroke: this.strokeColor,
        strokeWidth: this.baseStrokeWidth / this.fitScale,
        lineCap: 'round',
        lineJoin: 'round',
        tension: 0.4,
        bezier: true,
        points: [pos.x, pos.y],
        name: 'pen-line'
      });
    }
    this.drawLayer.add(this.currentLine);
  }

  private handlePointerMove(): void {
    if (!this.isDrawing || !this.currentLine || !this.drawLayer) {
      return;
    }
    const pos = this.drawLayer.getRelativePointerPosition();
    if (!pos) {
      return;
    }
    if (this.gridMode) {
      this.extendGridLine(pos.x, pos.y);
    } else {
      this.currentLine.points(this.currentLine.points().concat([pos.x, pos.y]));
    }
    this.drawLayer.batchDraw();
  }

  /**
   * Extend the current line with segments snapped to the grid and constrained to
   * horizontal, vertical or 45° diagonal directions. A move that is neither is
   * decomposed into a 45° diagonal part plus a straight (H/V) remainder.
   */
  private extendGridLine(rawX: number, rawY: number): void {
    if (!this.currentLine) {
      return;
    }
    const snapped = this.snapToGrid(rawX, rawY);
    const pts = this.currentLine.points();
    const lastX = pts[pts.length - 2];
    const lastY = pts[pts.length - 1];

    if (snapped.x === lastX && snapped.y === lastY) {
      return;
    }

    const dx = snapped.x - lastX;
    const dy = snapped.y - lastY;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);

    if (adx === 0 || ady === 0 || adx === ady) {
      // Already horizontal, vertical or a perfect 45° diagonal.
      this.currentLine.points(pts.concat([snapped.x, snapped.y]));
    } else {
      // Diagonal portion (45°) up to the smaller delta, then a straight remainder.
      const d = Math.min(adx, ady);
      const diagX = lastX + Math.sign(dx) * d;
      const diagY = lastY + Math.sign(dy) * d;
      this.currentLine.points(pts.concat([diagX, diagY, snapped.x, snapped.y]));
    }
  }

  private handlePointerUp(): void {
    this.isDrawing = false;
    this.currentLine = null;
  }

  private eraseAtPointer(): void {
    if (!this.stage || !this.drawLayer) {
      return;
    }
    const pointer = this.stage.getPointerPosition();
    if (!pointer) {
      return;
    }
    const shape = this.drawLayer.getIntersection(pointer);
    if (shape) {
      let node: Konva.Node = shape;
      while (node.getParent() && node.getParent() !== this.drawLayer) {
        node = node.getParent() as Konva.Node;
      }
      if (node === this.transformer) {
        return;
      }
      if (node === this.selectedNode) {
        this.deselectNode();
      }
      node.destroy();
      this.drawLayer.draw();
    }
  }

  // ============================================================
  // Shapes
  // ============================================================
  addShape(tool: DrawTool): void {
    if (!this.drawLayer) {
      return;
    }
    const cx = this.logicalWidth / 2;
    const cy = this.logicalHeight / 2;
    const unit = Math.min(this.logicalWidth, this.logicalHeight);
    const strokeW = this.baseStrokeWidth / this.fitScale;
    let node: Konva.Group | Konva.Shape | null = null;

    switch (tool) {
      case 'rect-landscape':
        node = new Konva.Rect({
          x: cx - unit * 0.2, y: cy - unit * 0.12, width: unit * 0.4, height: unit * 0.24,
          stroke: this.strokeColor, strokeWidth: strokeW, draggable: true, name: 'shape'
        });
        break;
      case 'rect-portrait':
        node = new Konva.Rect({
          x: cx - unit * 0.12, y: cy - unit * 0.2, width: unit * 0.24, height: unit * 0.4,
          stroke: this.strokeColor, strokeWidth: strokeW, draggable: true, name: 'shape'
        });
        break;
      case 'square':
        node = new Konva.Rect({
          x: cx - unit * 0.15, y: cy - unit * 0.15, width: unit * 0.3, height: unit * 0.3,
          stroke: this.strokeColor, strokeWidth: strokeW, draggable: true, name: 'shape keep-ratio'
        });
        break;
      case 'wall-h': {
        const thickness = Math.max(strokeW * 3, unit * 0.012);
        node = new Konva.Rect({
          x: cx - unit * 0.25, y: cy - thickness / 2, width: unit * 0.5,
          height: thickness, fill: this.strokeColor, draggable: true, name: 'shape fill line line-h'
        });
        break;
      }
      case 'wall-v': {
        const thickness = Math.max(strokeW * 3, unit * 0.012);
        node = new Konva.Rect({
          x: cx - thickness / 2, y: cy - unit * 0.25, width: thickness,
          height: unit * 0.5, fill: this.strokeColor, draggable: true, name: 'shape fill line line-v'
        });
        break;
      }
      case 'l-left':
      case 'l-right':
        node = this.createLShape(tool, cx, cy, unit * 0.4, strokeW);
        break;
      case 'door':
        node = this.createDoorShape(cx, cy, unit * 0.18, strokeW);
        break;
      default:
        return;
    }

    if (node) {
      this.drawLayer.add(node as any);
      this.drawLayer.draw();
      // Switch to the select tool so the new shape can be moved and resized,
      // and select it immediately so the resize handle is visible.
      this.setTool('pan');
      this.selectNode(node);
    }
  }

  private createDoorShape(cx: number, cy: number, size: number, strokeW: number): Konva.Group {
    const group = new Konva.Group({ x: cx, y: cy, draggable: true, name: 'shape door' });
    group.add(new Konva.Arc({
      innerRadius: size, outerRadius: size, angle: 90, rotation: -90,
      stroke: this.strokeColor, strokeWidth: strokeW
    }));
    group.add(new Konva.Line({
      points: [0, 0, 0, -size], stroke: this.strokeColor, strokeWidth: strokeW
    }));
    return group;
  }

  /** Create an L-shaped outline. 'l-left' is the letter L; 'l-right' is mirrored. */
  private createLShape(tool: 'l-left' | 'l-right', cx: number, cy: number, size: number, strokeW: number): Konva.Line {
    const W = size;
    const H = size;
    const t = size * 0.4; // arm thickness
    const points = tool === 'l-left'
      ? [0, 0, t, 0, t, H - t, W, H - t, W, H, 0, H]
      : [W, 0, W - t, 0, W - t, H - t, 0, H - t, 0, H, W, H];
    return new Konva.Line({
      x: cx - W / 2,
      y: cy - H / 2,
      points,
      closed: true,
      stroke: this.strokeColor,
      strokeWidth: strokeW,
      strokeScaleEnabled: false,
      draggable: true,
      name: 'shape lshape'
    });
  }

  // ============================================================
  // Start mode (blank vs photo)
  // ============================================================
  async startBlank(): Promise<void> {
    this.showStartChoice = false;
    this.logicalWidth = 1654;
    this.logicalHeight = 2339;
    this.computeFit();
    this.applyLayerTransforms();
    this.buildGrid();
    await this.loadIndicators();
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    this.showStartChoice = false;
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    this.pendingBackgroundFile = new File([file], backgroundFilename('plan', ext), { type: file.type });
    const reader = new FileReader();
    reader.onload = async () => {
      await this.setBackgroundFromDataUrl(reader.result as string, true);
      await this.loadIndicators();
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  private setBackgroundFromDataUrl(dataUrl: string, adoptDimensions: boolean): Promise<void> {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (adoptDimensions && img.width > 0 && img.height > 0) {
          this.logicalWidth = img.width;
          this.logicalHeight = img.height;
        }
        this.computeFit();
        this.applyLayerTransforms();
        this.buildGrid();
        if (this.bgImageNode) {
          this.bgImageNode.destroy();
        }
        this.bgImageNode = new Konva.Image({
          image: img, x: 0, y: 0, width: this.logicalWidth, height: this.logicalHeight, listening: false
        });
        this.bgLayer?.add(this.bgImageNode);
        this.bgLayer?.draw();
        this.hasBackground = true;
        this.repositionMarkers();
        resolve();
      };
      img.onerror = () => resolve();
      img.src = dataUrl;
    });
  }

  // ============================================================
  // Indicators
  // ============================================================
  private async loadIndicators(): Promise<void> {
    if (!this.standortId) {
      return;
    }
    try {
      const list: any = await firstValueFrom(this.objektStrukturService.getRoomIndikatorenByStandort(this.standortId));
      const all: any[] = Array.isArray(list) ? list : (list?.data || list?.items || []);
      // Only work with the indicators that belong to the sektion this plan is
      // drawn for – not every indicator of the whole standort.
      this.allRoomIndikatoren = await this.filterBySektion(all);
      this.allRoomIndikatoren
        .filter((ri) => this.hasPosition(ri))
        .forEach((ri) => this.addMarker(ri, ri.position_x, ri.position_y, false));
      this.markerLayer?.draw();
    } catch (error) {
      console.error('Error loading room indikatoren:', error);
    }
  }

  /**
   * Restrict a list of room-indikatoren to the rooms that belong to the current
   * sektion. Room-indikatoren reference a room (room_id) and each room belongs
   * to a sektion, so we resolve the sektion's rooms and keep only matching ones.
   * If the sektion cannot be resolved, the list is returned unchanged.
   */
  private async filterBySektion(indikatoren: any[]): Promise<any[]> {
    if (!this.sektionId || !Array.isArray(indikatoren) || indikatoren.length === 0) {
      return indikatoren;
    }
    try {
      const res: any = await firstValueFrom(this.objektStrukturService.getRaeume(this.sektionId));
      const rooms: any[] = Array.isArray(res) ? res : (res?.data || res?.items || []);
      const roomIds = new Set(rooms.map((r) => String(r.id)));
      if (roomIds.size === 0) {
        return indikatoren;
      }
      return indikatoren.filter((ri) => roomIds.has(String(ri.room_id)));
    } catch (error) {
      console.error('Error resolving sektion rooms for indikator filter:', error);
      return indikatoren;
    }
  }

  private hasPosition(ri: any): boolean {
    return ri.position_x !== null && ri.position_x !== undefined &&
           ri.position_y !== null && ri.position_y !== undefined;
  }

  private isPlaced(roomIndikatorId: string): boolean {
    return this.placedMarkers.some((m) => m.roomIndikatorId === roomIndikatorId);
  }

  private addMarker(indicator: any, logicalX: number, logicalY: number, persistNow: boolean): void {
    if (!this.markerLayer || this.isPlaced(indicator.id)) {
      return;
    }
    const canvasPos = this.logicalToCanvas(logicalX, logicalY);
    const group = new Konva.Group({ x: canvasPos.x, y: canvasPos.y, draggable: true, name: 'marker' });
    group.setAttr('logicalX', logicalX);
    group.setAttr('logicalY', logicalY);
    this.buildMarkerSymbol(group, indicator);

    group.on('dragend', () => {
      const logical = this.canvasToLogical(group.x(), group.y());
      group.setAttr('logicalX', logical.x);
      group.setAttr('logicalY', logical.y);
      this.persistMarkerPosition(indicator.id, logical.x, logical.y);
    });

    this.markerLayer.add(group);
    this.placedMarkers.push({ roomIndikatorId: indicator.id, indicator, group });
    if (persistNow) {
      this.persistMarkerPosition(indicator.id, logicalX, logicalY);
    }
    this.markerLayer.draw();
  }

  private buildMarkerSymbol(group: Konva.Group, indicator: any): void {
    const size = 28;
    const half = size / 2;
    const symbolId = indicator?.indikator?.symbol_id || indicator?.symbol_id || '1';
    const symbolUrl = this.getSymbolUrl(symbolId);
    const color = this.getSymbolColor(symbolId);

    const placeholder = new Konva.Circle({
      radius: half, fill: color, opacity: 0.85, stroke: '#ffffff', strokeWidth: 2
    });
    group.add(placeholder);

    if (symbolUrl) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (!ctx) {
          return;
        }
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        ctx.globalCompositeOperation = 'source-atop';
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, size, size);
        ctx.globalCompositeOperation = 'source-over';
        placeholder.destroy();
        const colored = new Konva.Image({
          image: canvas, x: -half, y: -half, width: size, height: size,
          shadowColor: 'black', shadowBlur: 3, shadowOffset: { x: 1, y: 1 }, shadowOpacity: 0.3
        });
        group.add(colored);
        this.addNumberBadge(group, indicator, half);
        this.markerLayer?.draw();
      };
      img.onerror = () => {
        this.addNumberBadge(group, indicator, half);
        this.markerLayer?.draw();
      };
      img.src = symbolUrl;
    } else {
      this.addNumberBadge(group, indicator, half);
    }
  }

  private addNumberBadge(group: Konva.Group, indicator: any, half: number): void {
    const num = indicator?.indikator_number ?? indicator?.room_indikator?.indikator_number;
    const label = num === null || num === undefined ? '-' : num.toString().padStart(3, '0');
    const badgeWidth = 22;
    const badgeHeight = 12;
    const badge = new Konva.Group({ x: -badgeWidth / 2, y: half + 2 });
    badge.add(new Konva.Rect({
      width: badgeWidth, height: badgeHeight, fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 1, cornerRadius: 3
    }));
    badge.add(new Konva.Text({
      text: label, fontSize: 9, fontStyle: 'bold', fill: '#ffffff', align: 'center',
      verticalAlign: 'middle', width: badgeWidth, height: badgeHeight, y: 1
    }));
    group.add(badge);
  }

  private repositionMarkers(): void {
    this.placedMarkers.forEach((m) => {
      const lx = m.group.getAttr('logicalX');
      const ly = m.group.getAttr('logicalY');
      m.group.position(this.logicalToCanvas(lx, ly));
    });
    this.markerLayer?.batchDraw();
  }

  private persistMarkerPosition(roomIndikatorId: string, logicalX: number, logicalY: number): void {
    this.objektStrukturService.updateRoomIndikator(roomIndikatorId, {
      position_x: Math.round(logicalX),
      position_y: Math.round(logicalY)
    }).subscribe({
      next: () => {
        const ri = this.allRoomIndikatoren.find((r) => r.id === roomIndikatorId);
        if (ri) {
          ri.position_x = Math.round(logicalX);
          ri.position_y = Math.round(logicalY);
        }
      },
      error: (err) => console.error('Error saving marker position:', err)
    });
  }

  private getSymbolUrl(symbolId: string): string | null {
    const map = (environment as any).indikatorSymbolMap || {};
    const key = map[symbolId];
    if (!key) {
      return null;
    }
    const base = (environment as any).indikatorSymbolBasePath || '/assets/symbols';
    return `${base}/${key}.svg`;
  }

  private getSymbolColor(symbolId: string): string {
    const map = (environment as any).indikatorSymbolColorMap || {};
    return map[symbolId] || '#3b82f6';
  }

  // ============================================================
  // Indicator picker (existing + create new)
  // ============================================================
  openIndicatorPicker(): void {
    if (!this.sektionId) {
      // No sektion resolved yet – try to auto-resolve from the standort, then retry.
      if (this.standortId) {
        this.objektStrukturService.getSektionenByStandort(this.standortId).subscribe({
          next: (res: any) => {
            const list = Array.isArray(res) ? res : (res?.data || []);
            if (list.length > 0) {
              this.sektionId = list[0].id;
              this.openIndicatorPicker();
            } else {
              this.snackBar.open('Keine Sektion vorhanden – bitte zuerst Gebäude/Sektion/Raum anlegen', 'Schließen', { duration: 4000 });
            }
          },
          error: () => this.snackBar.open('Keine Sektion ausgewählt', 'Schließen', { duration: 3000 })
        });
      } else {
        this.snackBar.open('Keine Sektion ausgewählt', 'Schließen', { duration: 3000 });
      }
      return;
    }
    try {
      const existing = this.allRoomIndikatoren.filter((ri) => !this.isPlaced(ri.id));
      const ref = this.dialog.open(PlanDrawIndicatorDialogComponent, {
        width: '480px',
        maxWidth: '92vw',
        data: { standortId: this.standortId, sektionId: this.sektionId, existing }
      });
      ref.afterClosed().subscribe((result: PlanDrawIndicatorDialogResult | undefined) => {
        if (!result) {
          return;
        }
        if (result.mode === 'existing' && result.indicator) {
          this.addMarker(result.indicator, this.logicalWidth / 2, this.logicalHeight / 2, true);
        } else if (result.mode === 'create') {
          this.createIndicator(result.indikatorId, result.roomId);
        }
      });
    } catch (e) {
      console.error('Failed to open indicator dialog:', e);
      this.snackBar.open('Indikator-Dialog konnte nicht geöffnet werden', 'Schließen', { duration: 3000 });
    }
  }

  private async createIndicator(indikatorId: string, roomId: string): Promise<void> {
    try {
      const created: any = await firstValueFrom(this.objektStrukturService.createRoomIndikator({
        room_id: roomId,
        indikator_id: indikatorId,
        name: 'Indikator',
        schwellenwert: '0',
        einheit: '',
        mehr_weniger: '',
        current_value: '',
        status: 1
      } as any));
      const newId = created?.id || created?.data?.id;
      if (!newId) {
        this.snackBar.open('Indikator konnte nicht erstellt werden', 'Schließen', { duration: 3000 });
        return;
      }
      if (this.standortId) {
        try {
          await firstValueFrom(this.objektStrukturService.assignIndikatorNumbers(this.standortId));
        } catch {
          // numbering is best-effort
        }
      }
      const newIndicator = { ...created, id: newId };
      this.allRoomIndikatoren.push(newIndicator);
      this.addMarker(newIndicator, this.logicalWidth / 2, this.logicalHeight / 2, true);
      this.snackBar.open('Indikator erstellt', 'Schließen', { duration: 2000 });
    } catch (error) {
      console.error('Error creating indikator:', error);
      this.snackBar.open('Fehler beim Erstellen', 'Schließen', { duration: 3000 });
    }
  }

  // ============================================================
  // Load existing plan (edit mode) + reconstruct drawing
  // ============================================================
  private async loadExistingPlan(): Promise<void> {
    if (!this.planId) {
      return;
    }
    try {
      this.plan = await firstValueFrom(this.planService.getPlan(this.planId));
      if (this.plan) {
        this.clientId = this.plan.client_id || null;
        this.standortId = this.standortId || this.plan.standort_id || null;
        if (!this.sektionId && this.plan.sektion_id) {
          this.sektionId = this.plan.sektion_id;
        }
        this.existingCompositeUploadId = this.plan.upload_id || null;
      }

      const uploads = await firstValueFrom(this.planService.getPlanUploads(this.planId));
      const bgUpload = (uploads || []).find((u) => isBackgroundFile(u.original_filename));
      const drawUpload = (uploads || []).find((u) => isDrawJsonFile(u.original_filename));

      if (bgUpload) {
        this.backgroundUploadId = bgUpload.id;
        try {
          const url = await this.buildSignedUrl(bgUpload.id);
          if (url) {
            await this.setBackgroundFromDataUrl(url, true);
          }
        } catch (e) {
          console.error('Error loading background:', e);
        }
      }

      if (drawUpload) {
        this.existingDrawJsonUploadId = drawUpload.id;
        try {
          const url = await this.buildSignedUrl(drawUpload.id);
          if (url) {
            const text = await firstValueFrom(this.http.get(url, { responseType: 'text' }));
            const envelope = parseDrawEnvelope(text);
            if (envelope) {
              this.reconstructDrawing(envelope, !bgUpload);
            }
          }
        } catch (e) {
          console.error('Error loading drawing JSON:', e);
        }
      }

      await this.loadIndicators();
    } catch (error) {
      console.error('Error loading existing plan:', error);
      this.snackBar.open('Plan konnte nicht geladen werden', 'Schließen', { duration: 3000 });
    }
  }

  private async buildSignedUrl(id: number): Promise<string | null> {
    const res = await firstValueFrom(this.dokumenteService.signDokumentUrl(id, 300));
    if (res && res.url) {
      const base = environment.apiUrl.replace(/\/$/, '').replace(/\/v5$/, '');
      return `${base}${res.url}`;
    }
    return null;
  }

  private reconstructDrawing(envelope: any, adoptDimensions: boolean): void {
    if (!this.drawLayer) {
      return;
    }
    if (adoptDimensions && envelope.logicalWidth && envelope.logicalHeight) {
      this.logicalWidth = envelope.logicalWidth;
      this.logicalHeight = envelope.logicalHeight;
      this.computeFit();
      this.applyLayerTransforms();
      this.buildGrid();
    }
    const children = envelope?.konva?.children || [];
    children.forEach((child: any) => {
      try {
        const node = Konva.Node.create(JSON.stringify(child));
        this.drawLayer!.add(node as any);
      } catch (e) {
        console.warn('Could not reconstruct draw node:', e);
      }
    });
    this.drawLayer.draw();
  }

  // ============================================================
  // Save
  // ============================================================
  async onSave(): Promise<void> {
    if (this.isSaving || this.showStartChoice) {
      return;
    }
    if (!this.standortId) {
      this.snackBar.open('Kein Standort angegeben', 'Schließen', { duration: 3000 });
      return;
    }
    if (!this.sektionId) {
      this.snackBar.open('Keine Sektion ausgewählt', 'Schließen', { duration: 3000 });
      return;
    }
    this.isSaving = true;
    try {
      // 1) Create or reuse the plan record.
      if (!this.planId) {
        await this.ensureClientId();
        const bezeichnung = `Zeichnung ${new Date().toLocaleString('de-DE')}`;
        const created = await firstValueFrom(this.planService.createPlan({
          bezeichnung,
          sektion_id: this.sektionId,
          standort_id: this.standortId,
          client_id: this.clientId || undefined
        } as any));
        this.plan = created;
        this.planId = created.id;
      }

      const bezeichnung = this.plan?.bezeichnung || `Zeichnung ${this.planId}`;

      // 2) Upload the original background photo (photo mode, first save only).
      if (this.pendingBackgroundFile && !this.backgroundUploadId) {
        const ext = (this.pendingBackgroundFile.name.split('.').pop() || 'jpg').toLowerCase();
        const bgFile = new File([this.pendingBackgroundFile], backgroundFilename(bezeichnung, ext), {
          type: this.pendingBackgroundFile.type
        });
        const bgId = await firstValueFrom(this.dokumenteService.uploadViaFilePond(bgFile, 'plan', this.planId!));
        if (bgId && !isNaN(bgId)) {
          this.backgroundUploadId = bgId;
        }
        this.pendingBackgroundFile = null;
      }

      // 3) Render the clean base image (background + drawing, NO markers) and set
      //    it as the plan's upload_id. This is what the webapp interactive plan
      //    overlays the draggable indicators on top of.
      const baseFile = this.exportBaseFile(bezeichnung);
      let baseUploadId: number | null = null;
      if (baseFile) {
        if (this.isEditMode && this.existingCompositeUploadId) {
          try {
            await firstValueFrom(this.dokumenteService.deleteDokument(this.existingCompositeUploadId));
          } catch {
            // best-effort
          }
        }
        const compositeId = await firstValueFrom(this.dokumenteService.uploadViaFilePond(baseFile, 'plan', this.planId!));
        if (compositeId && !isNaN(compositeId)) {
          await firstValueFrom(this.planService.updatePlan(this.planId!, { upload_id: compositeId }));
          this.existingCompositeUploadId = compositeId;
          baseUploadId = compositeId;
        }
      }

      // 3b) Render the indicator overlay on top of the base and store it as the
      //     -iplan companion (stable image for viewers, previews and PDF). The
      //     API derives the -iplan filename from the base upload and replaces any
      //     previous one on each save.
      if (baseUploadId) {
        const iplanDataUrl = this.renderStageDataUrl(true);
        if (iplanDataUrl) {
          try {
            await firstValueFrom(this.planService.saveInteractivePlan(baseUploadId, iplanDataUrl, 'png', this.planId!));
          } catch (e) {
            console.warn('Interactive-plan (-iplan) render failed:', e);
          }
        }
      }

      // 4) Upload the re-editable -draw.json (replace previous in edit mode).
      if (this.existingDrawJsonUploadId) {
        try {
          await firstValueFrom(this.dokumenteService.deleteDokument(this.existingDrawJsonUploadId));
        } catch {
          // best-effort
        }
        this.existingDrawJsonUploadId = null;
      }
      const drawFile = this.exportDrawJsonFile(bezeichnung);
      const drawId = await firstValueFrom(this.dokumenteService.uploadViaFilePond(drawFile, 'plan', this.planId!));
      if (drawId && !isNaN(drawId)) {
        this.existingDrawJsonUploadId = drawId;
      }

      // 5) Activate the plan for its sektion (the drawing becomes the interactive plan).
      try {
        await firstValueFrom(this.planService.activatePlan(this.planId!));
      } catch (e) {
        console.warn('Plan activation failed:', e);
      }

      // 6) Final flush of all placed marker positions.
      this.placedMarkers.forEach((m) => {
        this.persistMarkerPosition(m.roomIndikatorId, m.group.getAttr('logicalX'), m.group.getAttr('logicalY'));
      });

      this.snackBar.open('Plan gespeichert', 'Schließen', { duration: 3000 });
      this.navigateBack();
    } catch (error) {
      console.error('Error saving plan:', error);
      this.snackBar.open('Fehler beim Speichern', 'Schließen', { duration: 3500 });
    } finally {
      this.isSaving = false;
    }
  }

  private async ensureClientId(): Promise<void> {
    if (this.clientId || !this.standortId) {
      return;
    }
    try {
      const res = await firstValueFrom(this.planService.getPlansByStandort(this.standortId));
      const withClient = (res?.data || []).find((p) => p.client_id);
      if (withClient) {
        this.clientId = withClient.client_id;
      }
    } catch {
      // backend may derive client_id from the standort
    }
  }

  /**
   * Render the stage to a PNG data URL over the fixed logical page region
   * ([0,0 .. logicalWidth,logicalHeight]) at a 1:1 logical->pixel ratio. This
   * keeps the exported image's pixels aligned with the logical coordinate system
   * used for indicator positions, so the webapp interactive plan can overlay the
   * DB markers exactly where the baked -iplan markers are.
   *
   * @param includeMarkers when false the indicator marker layer is hidden,
   *   producing the clean base image (background + drawing only).
   */
  private renderStageDataUrl(includeMarkers: boolean): string | null {
    if (!this.stage) {
      return null;
    }
    // Never bake the resize handle or grid overlay into the exported image.
    this.deselectNode();
    const gridWasVisible = !!this.gridLayer?.visible();
    if (this.gridLayer) {
      this.gridLayer.visible(false);
    }
    const markerWasVisible = this.markerLayer ? this.markerLayer.visible() : true;
    if (this.markerLayer) {
      this.markerLayer.visible(includeMarkers);
    }
    // Bake an opaque white background into the export so the PNG is never
    // transparent (transparent areas render as black on dark surfaces).
    const whiteBg = new Konva.Rect({
      x: 0,
      y: 0,
      width: this.logicalWidth,
      height: this.logicalHeight,
      fill: '#ffffff',
      listening: false
    });
    this.bgLayer?.add(whiteBg);
    whiteBg.moveToBottom();
    const prevScale = this.stage.scaleX();
    const prevPos = this.stage.position();
    try {
      this.stage.scale({ x: 1, y: 1 });
      this.stage.position({ x: 0, y: 0 });
      // Recompute the page-fit transform so the capture always covers the ENTIRE
      // logical page, independent of the user's current zoom/pan or a resized
      // editor panel (which could otherwise leave stale offset/scale values and
      // export only the visible portion).
      this.computeFit();
      this.applyLayerTransforms();
      this.repositionMarkers();
      this.bgLayer?.batchDraw();
      this.stage.batchDraw();
      // 1:1 logical->pixel (no clamp) so base-image pixels equal logical units.
      const pixelRatio = this.fitScale > 0 ? 1 / this.fitScale : 1;
      return this.stage.toDataURL({
        mimeType: 'image/png',
        x: this.offsetX,
        y: this.offsetY,
        width: this.logicalWidth * this.fitScale,
        height: this.logicalHeight * this.fitScale,
        pixelRatio
      });
    } catch (e) {
      console.error('Error rendering stage:', e);
      return null;
    } finally {
      whiteBg.destroy();
      if (this.markerLayer) {
        this.markerLayer.visible(markerWasVisible);
      }
      this.stage.scale({ x: prevScale, y: prevScale });
      this.stage.position(prevPos);
      if (this.gridLayer) {
        this.gridLayer.visible(gridWasVisible);
      }
      this.bgLayer?.batchDraw();
      this.stage.batchDraw();
    }
  }

  /** Clean base image (background + drawing, NO indicator markers) for upload_id. */
  private exportBaseFile(bezeichnung: string): File | null {
    const dataUrl = this.renderStageDataUrl(false);
    if (!dataUrl) {
      return null;
    }
    return this.dataUrlToFile(dataUrl, `${this.sanitize(bezeichnung)}.png`);
  }

  private exportDrawJsonFile(bezeichnung: string): File {
    // Detach the transformer so it is not serialized into the drawing JSON,
    // then re-attach it afterwards.
    this.deselectNode();
    let layerJson = '{}';
    if (this.drawLayer) {
      if (this.transformer) {
        this.transformer.remove();
      }
      layerJson = this.drawLayer.toJSON();
      if (this.transformer) {
        this.drawLayer.add(this.transformer);
      }
    }
    const envelope = buildDrawEnvelope(layerJson, this.logicalWidth, this.logicalHeight);
    const blob = new Blob([JSON.stringify(envelope)], { type: 'application/json' });
    return new File([blob], drawJsonFilename(bezeichnung), { type: 'application/json' });
  }

  private dataUrlToFile(dataUrl: string, fileName: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  }

  private sanitize(bezeichnung: string): string {
    return (bezeichnung || 'plan')
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '_')
      .replace(/\s+/g, '_') || 'plan';
  }
}
